<template>
  <div id="auth-page" class="auth-container">
    <el-row>
      <el-col class="formCardTu" :span="12">
        <video autoplay loop muted playsinline preload="none"
          :src="loginVideoSrc"
          poster="../../assets/images/login.jpg"></video>
        <div class="videoText">
          <h3 class="f40">智能创造，一键生成-您的AI生产力引擎</h3>
          <p class="f18">快速捕捉图片中的主体元素，结合想象与创意，生成高质量直通车图片提升点击率和转化率</p>
        </div>
      </el-col>
      <el-col class="formCard" :span="12">
        <el-card class="auth-card boxShadow borderRadius">
          <!-- 登录/注册切换 -->
          <el-tabs v-model="activeTab" type="card" @tab-click="handleTabChange">
            <el-tab-pane class="tabText f22" label="登录" name="login"></el-tab-pane>
          </el-tabs>

          <!-- 登录表单 -->
          <el-form v-if="activeTab === 'login'" ref="loginFormRef" :model="loginForm" :rules="loginRules">
            <el-form-item prop="account">
              <i class="iconfont icon-shouji yellow"></i>
              <el-input v-model="loginForm.account" placeholder="请输入账号" @blur="handleAccountBlur"></el-input>
            </el-form-item>
            <el-form-item v-if="verification" prop="inviteCode">
              <i class="iconfont icon-yaoqingma yellow"></i>
              <el-input v-model="loginForm.inviteCode" placeholder="请输入邀请码（选填）"></el-input>
            </el-form-item>
            <el-form-item prop="password">
              <i class="iconfont icon-yanzhengma-yimidida- yellow"></i>
              <el-input v-model="loginForm.password" placeholder="请输入密码" :type="showPassword ? 'text' : 'password'">
                <!-- 密码显示/隐藏图标 -->
                <template #suffix>
                  <i class="iconfont" :class="showPassword ? 'icon-jurassic_openeyes' : 'icon-biyanjing-xianxing3-0'"
                    style="font-size: 20px; cursor: pointer;" @click="showPassword = !showPassword"></i>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item v-if="verification" prop="code">
              <i class="iconfont icon-yanzhengma-yimidida- yellow"></i>
              <el-input class="authCode" v-model="loginForm.code" placeholder="请输入验证码">
                <template #append>
                  <el-button :disabled="loginCountdown > 0" @click="sendLoginCode">
                    {{
                      loginCountdown > 0
                        ? `${loginCountdown}s后重试`
                        : "获取验证码"
                    }}
                  </el-button>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item prop="agreed">
              <el-checkbox v-model="loginForm.agreedJz">
                记住登录状态
              </el-checkbox>
              <el-checkbox v-model="loginForm.agreed">
                我已阅读并接受
                <a href="#" @click.prevent="showAgreement">《用户协议》</a> 和
                <a href="#" @click.prevent="showPrivacy">《隐私政策》</a>
              </el-checkbox>
            </el-form-item>

            <!-- 红色错误提示 -->
            <div v-if="errorMsg" class="error-tip f12" style="margin-bottom: 10px;color:#f56c6c;">{{ errorMsg }}</div>

            <el-button type="primary" @click="handleLogin" style="width: 100%" :loading="loginLoading">登录 /
              注册</el-button>
          </el-form>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router';

const router = useRouter();
let { proxy } = getCurrentInstance();

const LOGIN_VIDEO_URL = 'https://aicnoneplus.oss-cn-hangzhou.aliyuncs.com/video/project/login.17eed63a.mp4'
const loginVideoSrc = ref('')
let loginVideoTimer = null

// 先完成登录表单渲染，再加载背景视频，避免大文件参与首屏资源竞争。
const queueLoginVideo = () => {
  loginVideoTimer = window.setTimeout(() => {
    loginVideoSrc.value = LOGIN_VIDEO_URL
  }, 500)
}

onMounted(() => {
  if (document.readyState === 'complete') {
    queueLoginVideo()
    return
  }
  window.addEventListener('load', queueLoginVideo, { once: true })
})

// 当前激活的标签页
const activeTab = ref('login')

// 登录表单数据
const loginForm = ref({
  account: '',
  password: '',
  inviteCode: '',
  code: '',
  agreed: false,
  agreedJz: false
})
// 新增：控制密码显示/隐藏的状态
const showPassword = ref(false)

// 表单引用
const loginFormRef = ref(null)

// 加载状态
const loginLoading = ref(false)
// 倒计时
const loginCountdown = ref(0)
// 错误提示
const errorMsg = ref('')
const verification = ref(false)

// ==============================================
// ✅ 终极拦截：彻底屏蔽 ElMessage 弹窗（只在当前页生效）
// ==============================================
const originalError = ElMessage.error;
ElMessage.error = function () { }; // 空函数，不执行弹窗

onUnmounted(() => {
  window.removeEventListener('load', queueLoginVideo)
  if (loginVideoTimer !== null) {
    window.clearTimeout(loginVideoTimer)
  }
  ElMessage.error = originalError; // 离开页面恢复
});
// ==============================================

// 清空错误
const clearError = () => {
  errorMsg.value = ''
}

// 账号验证规则
const validateAccount = (rule, value, callback) => {
  const phoneRegex = /^1[3-9]\d{9}$/
  const dndxRegex = /^DNDX\d{3}$/
  if (!value) {
    callback(new Error('请输入账号'))
  } else if (!phoneRegex.test(value) && !dndxRegex.test(value)) {
    callback(new Error('账号格式不正确，支持手机号或DNDX+3位数字（如DNDX123）'))
  } else {
    callback()
  }
}

// 验证码验证规则
const validateCode = (rule, value, callback) => {
  if (verification.value && !value) {
    callback(new Error('请输入验证码'))
  } else if (value && !/^\d{6}$/.test(value)) {
    callback(new Error('验证码必须是6位数字'))
  } else {
    callback()
  }
}

// 密码验证规则
const validatePassword = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入密码'))
  } else if (value.length < 6) {
    callback(new Error('密码长度不能少于6位'))
  } else {
    callback()
  }
}

// 协议勾选验证
const validateAgreed = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请阅读并同意用户协议和隐私政策'))
  } else {
    callback()
  }
}

// 表单校验规则
const loginRules = {
  account: [{ validator: validateAccount, trigger: ['blur', 'submit'] }],
  password: [{ validator: validatePassword, trigger: ['blur', 'submit'] }],
  code: [{ validator: validateCode, trigger: 'blur' }],
  agreed: [{ validator: validateAgreed, trigger: 'submit' }]
}

// 发送验证码
const sendLoginCode = () => {
  clearError()
  loginFormRef.value.validateField('account', (valid) => {
    if (valid) {
      proxy.$http.post("/login/send?phoneNumber=" + loginForm.value.account)
        .then(res => {
          if (res.data.code === 0) {
            ElMessage.success('验证码已发送')
            loginCountdown.value = 60
            const timer = setInterval(() => {
              loginCountdown.value--
              if (loginCountdown.value <= 0) clearInterval(timer)
            }, 1000)
          }
        })
    }
  })
}

// 账号失焦验证
const handleAccountBlur = () => {
  clearError()
  if (!loginForm.value.account) return
  proxy.$http.post("/loginQuery/queryLoginName", {
    loginName: loginForm.value.account,
  }).then(res => {
    verification.value = !res.data.data
  })
}

// 登录
const handleLogin = () => {
  clearError()

  loginFormRef.value.validate((valid) => {
    if (!valid) return

    loginLoading.value = true
    proxy.$http.post("/loginByPassword", {
      loginName: loginForm.value.account,
      password: loginForm.value.password,
      loginDevice: 1,
      isLongTime: loginForm.value.agreedJz ? 1 : 0,
      userInputCode: loginForm.value.code,
      invitationCode: loginForm.value.inviteCode
    }).then(res => {
      loginLoading.value = false
      if (res.data.code === 0) {
        ElMessage.success("登录成功")
        localStorage.setItem("userid", res.data.data.employeeId)
        localStorage.setItem("departmentid", res.data.data.departmentId)
        localStorage.setItem("token", res.data.data.token)
        localStorage.setItem("avatar", res.data.data.avatar)
        const roleCodes = res.data.data.roleList.map(role => role.roleCode)
        localStorage.setItem('userRoleCodes', JSON.stringify(roleCodes))
        router.push(roleCodes.includes('training') ? '/model' : "/user")
      }
    }).catch(err => {
      loginLoading.value = false
      errorMsg.value = err?.msg || err?.message || '登录失败，请重试'
    })
  })
}

const showAgreement = () => {
  // 解析路由地址
  const routeUrl = router.resolve({
    path: '/useragreement'
  })
  // 新窗口打开
  window.open(routeUrl.href, '_blank')
}
const showPrivacy = () => {
  // 解析路由地址
  const routeUrl = router.resolve({
    path: '/privacypolicy'
  })
  // 新窗口打开
  window.open(routeUrl.href, '_blank')
}
const handleTabChange = () => {
  clearError()
  loginFormRef.value.clearValidate()
}
</script>

<style lang="less" scoped>
.el-row {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.el-col {
  border-radius: 4px;
}

.bg-purple-dark {
  background: #99a9bf;
}

.bg-purple {
  background: #d3dce6;
}

.bg-purple-light {
  background: #e5e9f2;
}

.grid-content {
  border-radius: 4px;
  min-height: 36px;
}

.row-bg {
  padding: 10px 0;
  background-color: #f9fafc;
}

.auth-container {
  height: 100vh;
  background: #1f2229;
  overflow: hidden;
}

.auth-card {
  width: 470px;
  padding: 4% 3% 6%;
}

.el-col,
.el-row {
  height: 100%;
}

.formCard {
  display: flex;
  justify-content: center;
  align-items: center;
}

.formCardTu {
  position: relative;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .videoText {
    position: absolute;
    width: 80%;
    top: 10%;
    left: 10%;
    color: #fff;

    h3 {
      font-weight: bold;
      margin-bottom: 20px;
    }

    p {
      font-weight: 300;
      width: 80%;
    }
  }
}

.el-card {
  background: none;
  border: none;
}

::v-deep .el-tabs__nav {
  text-align: center;
  float: none;
}

::v-deep .el-tabs--card>.el-tabs__header {
  border: none !important;
}

::v-deep .el-tabs--card>.el-tabs__header .el-tabs__nav {
  border: none !important;
}

::v-deep .el-tabs__item {
  color: #fff !important;
  border: none !important;
  font-size: 22px;
  width: 100%;
  opacity: 0.5;
}

::v-deep .el-tabs__item.is-active {
  opacity: 1;
}

::v-deep .el-input__inner {
  height: 50px;
  line-height: 50px;
  font-size: 16px;
  padding-left: 40px;
}

::v-deep .authCode .el-input__inner {
  width: 94%;
  border-radius: 6px;
}

::v-deep .el-input-group__append {
  border-radius: 6px;
  background: #fcc800;
  border-color: #fcc800;
  color: #fff;
  font-size: 16px;
  font-weight: 400;
  box-shadow: inherit;
}

::v-deep .el-input-group__append span {
  font-weight: 350;
}

.el-checkbox {
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  font-weight: 350;

  a {
    color: rgba(252, 200, 0, 0.6);
  }
}

::v-deep .el-checkbox__label {
  font-size: 16px;
}

::v-deep .el-checkbox__input {
  margin-top: -2px;
}

::v-deep .el-checkbox__input.is-checked+.el-checkbox__label {
  color: rgba(255, 255, 255, 0.6);
}

::v-deep .el-checkbox__input.is-checked .el-checkbox__inner,
::v-deep .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: #fcc800;
  border-color: #fcc800;
}

::v-deep .el-checkbox__input.is-focus .el-checkbox__inner,
::v-deep .el-checkbox__inner:hover {
  border-color: #fcc800;
}

::v-deep .el-input.is-active .el-input__inner,
::v-deep .el-input__inner:focus {
  border-color: #fcc800;
}

.el-button {
  height: 50px;
}

.el-button--primary {
  background-color: #fcc800;
  border-color: #fcc800;
  padding: 17px 20px;
  font-size: 18px;
  font-weight: 350;
}

.el-button--primary:focus,
.el-button--primary:hover {
  background: #eebd00;
  border-color: #eebd00;
  color: #fff;
}

.el-tabs {
  margin-bottom: 20px;
}

::v-deep .el-input__prefix {
  left: 10px;
}

::v-deep .el-input--prefix .el-input__inner {
  padding-left: 40px;
}

.el-form-item {
  position: relative;

  i {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 15px;
    font-size: 20px;
    z-index: 9;

    &.icon-yaoqingma {
      font-size: 13px;
    }
  }
}

.el-button.is-disabled,
.el-button.is-disabled:focus,
.el-button.is-disabled:hover {
  padding: 17px 20px;
  color: #c0c4cc;
  background-image: none;
  background-color: #fff;
  border-color: #ebeef5;
}

::v-deep .authCode .el-input__wrapper {
  margin-right: 4%;
  border-radius: 6px !important;
}

/* 新增：调整密码框suffix图标样式 */
::v-deep .el-input__suffix {
  right: 60px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  position: absolute;
}

::v-deep .el-input__suffix:hover {
  color: #fcc800;
}
</style>

<!--
  The authentication screen is intentionally a fixed dark split layout.
  Keep these rules unscoped and more specific than the workspace theme so
  switching the application to light mode cannot turn the dark card text or
  its primary action black.
-->
<style lang="less">
html body #app .auth-container {
  color: #fff;
  background: #1f2229;
  isolation: isolate;
}

html body #app .auth-container .formCardTu video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #0b0e13;
}

html body #app .auth-container .auth-card.el-card {
  color: #fff;
  background: #1f2229 !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04),
    0 16px 42px rgba(0, 0, 0, 0.3) !important;
}

html body #app .auth-container .auth-card .el-card__body {
  padding: 0;
}

html body #app .auth-container .auth-card .el-tabs__item,
html body #app .auth-container .auth-card .el-tabs__item.is-active {
  color: #fff !important;
}

html body #app .auth-container .auth-card .el-tabs__nav-wrap::after {
  display: none;
}

html body #app .auth-container .auth-card .el-input__wrapper {
  background: #fff !important;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1) inset !important;
}

html body #app .auth-container .auth-card .el-input__wrapper.is-focus {
  box-shadow: 0 0 0 1px #fcc800 inset,
    0 0 0 3px rgba(252, 200, 0, 0.14) !important;
}

html body #app .auth-container .auth-card .el-input__inner {
  color: #25272d !important;
  background: transparent !important;
}

html body #app .auth-container .auth-card .el-input__inner::placeholder {
  color: #a0a3aa !important;
}

html body #app .auth-container .auth-card .el-input__suffix,
html body #app .auth-container .auth-card .el-input__suffix .iconfont {
  color: #8d9199 !important;
}

html body #app .auth-container .auth-card .el-checkbox__label,
html body #app .auth-container .auth-card .el-checkbox__input.is-checked + .el-checkbox__label {
  color: rgba(255, 255, 255, 0.72) !important;
}

html body #app .auth-container .auth-card .el-checkbox__label a {
  color: #d9ad00 !important;
}

html body #app .auth-container .auth-card .el-checkbox__input.is-checked .el-checkbox__inner,
html body #app .auth-container .auth-card .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background: #fcc800 !important;
  border-color: #fcc800 !important;
}

html[data-theme] body #app #auth-page .auth-card .el-button--primary,
html[data-theme] body #app #auth-page .auth-card .el-button--primary:hover,
html[data-theme] body #app #auth-page .auth-card .el-button--primary:focus,
html[data-theme] body #app #auth-page .auth-card .el-button--primary:active {
  color: #fff !important;
  background: #fcc800 !important;
  background-image: none !important;
  border-color: #fcc800 !important;
  box-shadow: none !important;
}

html[data-theme] body #app #auth-page .auth-card .el-button--primary:hover,
html[data-theme] body #app #auth-page .auth-card .el-button--primary:focus {
  background: #eebd00 !important;
  border-color: #eebd00 !important;
}

html body #app .auth-container .auth-card .el-form-item__error {
  color: #ff7c7c !important;
}

@media (max-width: 900px) {
  html body #app .auth-container .formCardTu {
    position: absolute;
    inset: 0;
    width: 100%;
    max-width: none;
  }

  html body #app .auth-container .formCardTu::after {
    position: absolute;
    inset: 0;
    content: '';
    background: rgba(15, 17, 22, 0.58);
  }

  html body #app .auth-container .formCard {
    position: relative;
    width: 100%;
    max-width: none;
    flex: 0 0 100%;
    z-index: 1;
  }

  html body #app .auth-container .formCardTu .videoText {
    display: none;
  }

  html body #app .auth-container .auth-card {
    width: min(470px, calc(100vw - 32px));
  }
}
</style>
